export const QUICK_DEATH_MS = 10_000;
export const STABLE_WINDOW_MS = 5 * 60_000;

export type IceDeathState = 'disconnected' | 'failed';

export interface IceRestartDecision {
  action: 'restart';
  relay: boolean;
  stableReset: boolean;
  connectedLifetimeMs: number | null;
}

export interface IceReportDecision {
  action: 'report';
  cause: 'network_blocked' | 'connection_failed';
  connectedLifetimeMs: number | null;
}

export type IceRecoveryDecision = IceRestartDecision | IceReportDecision;

// A type alias, not an interface: it is assigned directly as a Sentry context,
// which requires an implicit index signature — interfaces don't get one.
export type IceRecoverySnapshot = {
  failureCount: number;
  relayActive: boolean;
  relayRestartUsed: boolean;
};

// Per-PeerConnection restart budget. Nothing here resets on 'connected' —
// a client that flaps and always reconnects must still reach the modal.
export class IceRecoveryLadder {
  private failureCount = 0;

  private relayActive = false;

  private relayRestartUsed = false;

  private connectedAt: number | null = null;

  private everConnected = false;

  private budgetBeforeLastDeath: Pick<
    IceRecoverySnapshot,
    'failureCount' | 'relayRestartUsed'
  > | null = null;

  get isRelayActive() {
    return this.relayActive;
  }

  get hasConnected() {
    return this.everConnected;
  }

  markConnected(now = Date.now()) {
    this.connectedAt = now;
    this.everConnected = true;
  }

  // A rung is consumed only by a restart that fires, so 'connected' cancelling a
  // pending debounce must give it back. `relayActive` stays: it is on the PC.
  forgiveUndispatchedDeath() {
    if (!this.budgetBeforeLastDeath) {
      return;
    }

    this.failureCount = this.budgetBeforeLastDeath.failureCount;
    this.relayRestartUsed = this.budgetBeforeLastDeath.relayRestartUsed;
    this.budgetBeforeLastDeath = null;
  }

  markRelayActive() {
    this.relayActive = true;
  }

  snapshot(): IceRecoverySnapshot {
    return {
      failureCount: this.failureCount,
      relayActive: this.relayActive,
      relayRestartUsed: this.relayRestartUsed,
    };
  }

  decide(
    state: IceDeathState,
    previouslyConnected: boolean,
    now = Date.now()
  ): IceRecoveryDecision {
    if (!previouslyConnected && !this.everConnected) {
      return { action: 'report', cause: 'network_blocked', connectedLifetimeMs: null };
    }

    const connectedLifetimeMs = this.connectedAt === null ? null : now - this.connectedAt;
    // A death consumes the lifetime it measured, or consecutive deaths with no
    // 'connected' between them each re-earn the stable-window reset forever.
    this.connectedAt = null;

    const stableReset = connectedLifetimeMs !== null && connectedLifetimeMs > STABLE_WINDOW_MS;

    if (stableReset) {
      this.failureCount = 0;
      this.relayRestartUsed = false;
    }

    // Snapshotted after the stable reset: forgiving this death must undo only
    // the death, not the lifetime the connection genuinely earned.
    this.budgetBeforeLastDeath = {
      failureCount: this.failureCount,
      relayRestartUsed: this.relayRestartUsed,
    };

    this.failureCount += 1;

    const restart = (relay: boolean): IceRestartDecision => ({
      action: 'restart',
      relay,
      stableReset,
      connectedLifetimeMs,
    });

    if (this.failureCount === 1) {
      const quickDeath = connectedLifetimeMs !== null && connectedLifetimeMs <= QUICK_DEATH_MS;
      return restart(state === 'failed' || quickDeath);
    }

    if (this.failureCount === 2) {
      return restart(true);
    }

    if (this.relayActive && !this.relayRestartUsed) {
      this.relayRestartUsed = true;
      return restart(true);
    }

    return { action: 'report', cause: 'connection_failed', connectedLifetimeMs };
  }
}
